import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';

// Component imports
import LandingPage from './components/LandingPage';
import ExplorePage from './components/ExplorePage';
import TimelinePage from './components/TimelinePage';
import QuizPage from './components/QuizPage';
import FlashcardsPage from './components/FlashcardsPage';
import AddEventPage from './components/AddEventPage';
import TeacherDashboard from './components/TeacherDashboard';
import ClassDetailPage from './components/ClassDetailPage';
import ClassPage from './components/ClassPage';
import StudentDashboard from './components/StudentDashboard';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import RoleSelector from './components/RoleSelector';
import ProtectedRoute from './components/ProtectedRoute';
import About from './components/About';
import Contact from './components/Contact';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import Footer from './components/Footer';
import BetaBanner from './components/BetaBanner';
import Navbar from './components/Navbar';

// Axios token injector
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Moved AppContent here to use useLocation properly
function AppContent({ user, setUser, activeRole, setActiveRole }) {
  const location = useLocation();

  return (
    <div className="bg-gray-900 min-h-screen text-gray-100 flex flex-col">
      <BetaBanner />
      {!['/login', '/register', '/student-dashboard'].includes(location.pathname) && (
        <Navbar user={user} setUser={setUser} setActiveRole={setActiveRole} />
      )}

      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<LandingPage user={user} setUser={setUser} setActiveRole={setActiveRole} />} />
          <Route path="/login" element={<LoginPage setUser={setUser} setActiveRole={setActiveRole} />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/role-selector" element={<RoleSelector setActiveRole={setActiveRole} />} />

          <Route
            path="/student-dashboard"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard setUser={setUser} setActiveRole={setActiveRole} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timeline"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <TimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quiz"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <QuizPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/flashcards"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <FlashcardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-event"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AddEventPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <ExplorePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <TeacherDashboard setUser={setUser} setActiveRole={setActiveRole} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:classId"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <ClassDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:id"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <ClassPage />
              </ProtectedRoute>
            }
          />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [activeRole, setActiveRole] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRole = localStorage.getItem('activeRole');

    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      if (storedRole && parsedUser.roles?.includes(storedRole)) {
        setActiveRole(storedRole);
      } else if (parsedUser.roles?.length === 1) {
        setActiveRole(parsedUser.roles[0]);
      } else {
        setActiveRole(null);
      }
    }
  }, []);

  return (
    <Router>
      <AppContent
        user={user}
        setUser={setUser}
        activeRole={activeRole}
        setActiveRole={setActiveRole}
      />
    </Router>
  );
}

export default App;
